import { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../../UserContext';
import TemplateKeyField, { keysDiffer } from './TemplateKeyField';
import { firestoreReportApi } from './reportApi';
import { buildReportPdfBlob, downloadBlob } from './reportPdf';
import {
  CS_LABELS,
  DA_LABELS,
  D_LABELS,
  EVAL_AREAS,
  HS_LABELS,
  REPORT_GROUPS,
  SA_LABELS,
  formatCodeLabel,
  formatPinnedCodesSummary,
  persistableCodes,
  withStubDefaults,
} from './reportCodes';
import {
  CS_VALUES,
  allPermutationKeys,
  assembleReport,
  canEditInterpretationTemplates,
  evaluationTemplateKeys,
  lookupProse,
  makeCautionKey,
  missingTemplateKeys,
  nearestTemplateKeys,
  stepMissingKey,
  versionedDocId,
} from './reportTemplates';

const DUPLICATE_SUGGESTION_LIMIT = 5;

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function formatTimestamp(value) {
  if (!value) return '—';
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function SelectField({ id, label, value, onChange, options, emptyLabel = 'Not scored (stub)' }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-gray-700">{label}</span>
      <select
        className="w-full rounded-md border border-gray-300 py-1.5 pl-2 pr-8 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        id={id}
        onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
        value={value ?? ''}
      >
        <option value="">{emptyLabel}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label} ({option.id})
          </option>
        ))}
      </select>
    </label>
  );
}

function toSelectOptions(labels) {
  return Object.entries(labels).map(([id, label]) => ({ id: Number(id), label }));
}

/**
 * Shared template CMS + generate + history UI. Callers pass an area-specific
 * scorer via scoreCodes. Cloud and legacy scorers stay separate.
 */
export default function EvalReport({
  area = EVAL_AREAS.CLOUD,
  title,
  description,
  scoreCodes,
  client,
  evaluationId,
  caseNumber,
  organizationId,
  api = firestoreReportApi,
  claims: claimsProp,
  allowSaOverrides = false,
}) {
  const user = useContext(UserContext);
  const [claims, setClaims] = useState(claimsProp ?? null);
  const [overrides, setOverrides] = useState({ cs: 0, hs: {}, d: {}, sa: {} });
  const [templates, setTemplates] = useState({ published: null, draft: null });
  const [draftKeys, setDraftKeys] = useState({});
  const [unlockedKey, setUnlockedKey] = useState(null);
  const [justSavedKey, setJustSavedKey] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState(null);
  const [catalogGroup, setCatalogGroup] = useState('FA');
  const [catalogKey, setCatalogKey] = useState(null);
  const [previewed, setPreviewed] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportsError, setReportsError] = useState(null);

  useEffect(() => {
    if (claimsProp) {
      setClaims(claimsProp);
      return;
    }
    if (!user?.getIdTokenResult) {
      setClaims(null);
      return;
    }
    let active = true;
    user.getIdTokenResult()
      .then((result) => {
        if (active) setClaims(result.claims ?? null);
      })
      .catch(() => {
        if (active) setClaims(null);
      });
    return () => {
      active = false;
    };
  }, [user, claimsProp]);

  const scored = useMemo(
    () => scoreCodes(overrides),
    [scoreCodes, overrides]
  );
  const publishedVersion = Number(templates.published?.version);
  const canPinVersion = Number.isFinite(publishedVersion) && publishedVersion >= 1;
  const keyed = useMemo(() => withStubDefaults(scored), [scored]);
  const sex = keyed.sex ?? scored.sex;
  const canEdit = canEditInterpretationTemplates(claims);
  const publishedKeys = templates.published?.keys ?? {};
  const workingKeys = canEdit ? draftKeys : publishedKeys;
  const neededKeys = useMemo(() => evaluationTemplateKeys(keyed), [keyed]);
  const missingForEval = useMemo(
    () => missingTemplateKeys(workingKeys, neededKeys),
    [workingKeys, neededKeys]
  );
  const assembledPreview = useMemo(
    () => assembleReport({
      codes: keyed,
      templateKeys: workingKeys,
      clientName: client?.Name ?? client?.name,
      sex,
      templateVersion: canEdit ? 'draft' : templates.published?.version,
      caseNumber,
    }),
    [keyed, workingKeys, client?.Name, client?.name, sex, canEdit, templates.published?.version, caseNumber]
  );
  const catalogKeys = useMemo(
    () => (catalogGroup === 'CS' ? CS_VALUES.map((cs) => makeCautionKey(cs)) : allPermutationKeys(catalogGroup)),
    [catalogGroup]
  );
  const catalogMissing = useMemo(
    () => missingTemplateKeys(workingKeys, catalogKeys),
    [workingKeys, catalogKeys]
  );

  useEffect(() => {
    let active = true;
    async function load() {
      if (!sex) {
        setTemplates({ published: null, draft: null });
        setDraftKeys({});
        return;
      }
      setTemplatesLoading(true);
      setTemplatesError(null);
      try {
        const loaded = await api.loadTemplatesForSex(sex);
        if (!active) return;
        setTemplates(loaded);
        setDraftKeys(loaded.draft?.keys ?? loaded.published?.keys ?? {});
        setUnlockedKey(null);
        setJustSavedKey(null);
        setPreviewed(false);
      } catch (error) {
        console.error('Failed to load interpretation templates:', error);
        if (active) setTemplatesError('Failed to load interpretation templates.');
      } finally {
        if (active) setTemplatesLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [api, sex]);

  // An open field must stay on screen. If its key is navigated away from
  // (catalog chip, override change), close it rather than leave every other
  // Edit / Duplicate blocked by a field the user cannot see.
  useEffect(() => {
    if (!unlockedKey) return;
    if (unlockedKey === catalogKey || neededKeys.includes(unlockedKey)) return;
    setUnlockedKey(null);
  }, [unlockedKey, catalogKey, neededKeys]);

  useEffect(() => {
    let active = true;
    async function loadReports() {
      if (!evaluationId) {
        setReports([]);
        return;
      }
      setReportsError(null);
      try {
        const loaded = await api.listReports({ evaluationId, clientId: client?.id });
        if (active) setReports(loaded);
      } catch (error) {
        console.error('Failed to load generated reports:', error);
        if (active) setReportsError('Failed to load report history.');
      }
    }
    loadReports();
    return () => {
      active = false;
    };
  }, [api, evaluationId, client?.id]);

  function setGroupOverride(kind, groupId, value) {
    setOverrides((current) => ({
      ...current,
      [kind]: { ...current[kind], [groupId]: value },
    }));
  }

  async function handleFieldSave(key, value) {
    if (!canEdit) return;
    const next = { ...draftKeys };
    if (value && value.trim()) next[key] = value;
    else delete next[key];
    setBusy(true);
    setStatus(null);
    try {
      await api.saveDraft({ sex, keys: next, userId: user?.uid });
      setDraftKeys(next);
      setTemplates((current) => ({
        ...current,
        draft: { ...(current.draft ?? {}), sex, status: 'draft', keys: next },
      }));
      setUnlockedKey(null);
      setJustSavedKey(key);
      setPreviewed(false);
    } catch (error) {
      console.error('Failed to save draft:', error);
      setStatus({ tone: 'error', text: 'Could not save the draft.' });
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    if (!canEdit || !previewed) return;
    setBusy(true);
    setStatus(null);
    try {
      await api.saveDraft({ sex, keys: draftKeys, userId: user?.uid });
      const published = await api.publishDraft({ sex, userId: user?.uid });
      setTemplates({
        published: { ...published, keys: draftKeys },
        draft: { sex, status: 'draft', version: published.version, keys: draftKeys },
      });
      setUnlockedKey(null);
      setJustSavedKey(null);
      setStatus({ tone: 'ok', text: `Published ${sex} template v${published.version}. New PDFs will pin this version.` });
    } catch (error) {
      console.error('Failed to publish draft:', error);
      setStatus({ tone: 'error', text: error.message || 'Could not publish the draft.' });
    } finally {
      setBusy(false);
    }
  }

  async function handleGenerate() {
    setBusy(true);
    setStatus(null);
    try {
      if (!canPinVersion) {
        throw new Error('Generate requires a published template version. Seed is insert-if-missing; publish a draft (empty is ok) so the PDF can pin vN.');
      }
      const snapshot = api.loadPublishedVersion
        ? await api.loadPublishedVersion(sex, publishedVersion)
        : templates.published;
      if (!snapshot || Number(snapshot.version) !== publishedVersion) {
        throw new Error(`Could not load immutable ${sex} v${publishedVersion}. History cannot pin latest prose.`);
      }
      const assembled = {
        ...assembleReport({
          codes: keyed,
          templateKeys: snapshot.keys ?? {},
          clientName: client?.Name ?? client?.name,
          sex,
          templateVersion: snapshot.version,
          caseNumber,
        }),
        area,
      };
      const pdfBlob = buildReportPdfBlob(assembled);
      const filename = `vasi-${area}-eval-${evaluationId || 'report'}.pdf`;
      const saved = await api.saveGeneratedReport({
        clientId: client?.id,
        evaluationId,
        organizationId,
        clientName: client?.Name ?? client?.name,
        sex,
        source: area,
        codes: persistableCodes(keyed),
        templateVersion: snapshot.version,
        templateId: snapshot.id ?? versionedDocId(sex, snapshot.version),
        pdfBlob,
        userId: user?.uid,
      });
      downloadBlob(pdfBlob, filename);
      setReports((current) => {
        if (current.some((report) => report.id === saved.id)) return current;
        return [{ id: saved.id, ...saved, createdAt: saved.createdAt ?? new Date() }, ...current];
      });
      setStatus({
        tone: 'ok',
        text: assembled.codesOnly
          ? `Generated a codes-only PDF pinned to ${sex} v${snapshot.version} (no prose in that version).`
          : `Generated PDF pinned to ${sex} template v${snapshot.version}.`,
      });
    } catch (error) {
      console.error('Failed to generate report:', error);
      setStatus({ tone: 'error', text: error.message || 'Could not generate the report.' });
    } finally {
      setBusy(false);
    }
  }

  const hasDirtyDraft = canEdit && keysDiffer(draftKeys, publishedKeys);

  function stepCatalog(direction) {
    const next = stepMissingKey({
      ordered: catalogKeys,
      missing: catalogMissing,
      current: catalogKey,
      direction,
    });
    if (next) setCatalogKey(next);
  }

  function renderKeyEditor(key) {
    const published = lookupProse(publishedKeys, key);
    const saved = lookupProse(draftKeys, key);
    return (
      <TemplateKeyField
        busy={busy}
        canEdit={canEdit}
        justSaved={justSavedKey === key}
        key={key}
        keyName={key}
        missing={!saved}
        onCancel={() => {
          if (unlockedKey === key) setUnlockedKey(null);
        }}
        onSave={(value) => handleFieldSave(key, value)}
        lockedElsewhere={Boolean(unlockedKey) && unlockedKey !== key}
        onUnlock={() => {
          if (unlockedKey && unlockedKey !== key) return false;
          setJustSavedKey(null);
          setUnlockedKey(key);
          return true;
        }}
        duplicateCandidates={canEdit
          ? nearestTemplateKeys(draftKeys, key, { limit: DUPLICATE_SUGGESTION_LIMIT })
          : []}
        published={published}
        savedValue={saved}
        unlocked={unlockedKey === key}
      />
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <div className="mt-1 text-sm text-gray-600">{description}</div>
      </div>

      {!scored.chartPathReady && (
        <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          Charts are not ready, so some DA cells may be unscored. Master comparison data is required.
        </div>
      )}

      <section>
        <h3 className="text-base font-medium text-gray-900">Scores</h3>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-700">Group</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">SA</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">DA</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">HS</th>
                <th className="px-3 py-2 text-left font-medium text-gray-700">D</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t">
                <td className="px-3 py-2 font-medium text-gray-900">Caution (CS)</td>
                <td className="px-3 py-2 text-gray-600" colSpan={4}>
                  {formatCodeLabel(CS_LABELS, keyed.caution.cs, { stub: keyed.caution.stub })}
                </td>
              </tr>
              {REPORT_GROUPS.map((group) => {
                const row = keyed.groups[group.id];
                return (
                  <tr className="border-t" key={group.id}>
                    <td className="px-3 py-2 font-medium text-gray-900">{group.label}</td>
                    <td className="px-3 py-2 text-gray-600">{formatCodeLabel(SA_LABELS, row.sa)}</td>
                    <td className="px-3 py-2 text-gray-600">{formatCodeLabel(DA_LABELS, row.da)}</td>
                    <td className="px-3 py-2 text-gray-600">{formatCodeLabel(HS_LABELS, row.hs, { stub: row.stub.hs })}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {group.hasD ? formatCodeLabel(D_LABELS, row.d, { stub: row.stub.d }) : ''}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 grid gap-4 rounded-md border border-dashed border-gray-300 p-4 md:grid-cols-3">
          <SelectField
            id="cs-override"
            label="CS override (stub)"
            onChange={(value) => setOverrides((current) => ({ ...current, cs: value ?? 0 }))}
            options={toSelectOptions(CS_LABELS)}
            value={overrides.cs}
          />
          {REPORT_GROUPS.map((group) => (
            <div className="space-y-2" key={group.id}>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{group.label} stubs</p>
              {allowSaOverrides && (
                <SelectField
                  id={`${group.id}-sa`}
                  emptyLabel="Unscored"
                  label="SA"
                  onChange={(value) => setGroupOverride('sa', group.id, value)}
                  options={toSelectOptions(SA_LABELS)}
                  value={overrides.sa[group.id] ?? keyed.groups[group.id]?.sa}
                />
              )}
              <SelectField
                id={`${group.id}-hs`}
                label="HS"
                onChange={(value) => setGroupOverride('hs', group.id, value)}
                options={toSelectOptions(HS_LABELS)}
                value={overrides.hs[group.id] ?? 0}
              />
              {group.hasD && (
                <SelectField
                  id={`${group.id}-d`}
                  label="D"
                  onChange={(value) => setGroupOverride('d', group.id, value)}
                  options={toSelectOptions(D_LABELS)}
                  value={overrides.d[group.id] ?? 0}
                />
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex flex-wrap items-center gap-2 text-base font-medium text-gray-900">
              <span>Interpretation templates</span>
              {hasDirtyDraft && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  Draft
                </span>
              )}
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Versioned Firestore collection <code className="rounded bg-gray-100 px-1">interpretation_templates</code>.
              Click Edit on a field to change it; field Save writes a draft. Publish is a separate control and always creates a new <code className="rounded bg-gray-100 px-1">{'{sex}_vN'}</code> —
              published versions are never overwritten. vasi_functions seed is insert-if-missing; fill gaps here, then publish.
              Female may be empty until seeded.
              {sex ? ` Viewing ${sex}.` : ''}
            </p>
          </div>
          <div className="text-sm text-gray-600">
            {templatesLoading ? 'Loading templates…' : (
              <>
                Published: {templates.published?.version != null ? `v${templates.published.version}` : 'none'}
                {canEdit && hasDirtyDraft && ' · unpublished draft'}
              </>
            )}
          </div>
        </div>

        {templatesError && <p className="mt-2 text-sm text-red-700">{templatesError}</p>}
        {!canEdit && (
          <p className="mt-2 text-sm text-gray-500">
            Template edit/publish is limited to template owners (Eric-class) or admins.
            You can still generate a PDF from the published version.
          </p>
        )}

        <div className="mt-4 space-y-3">
          <h4 className="text-sm font-medium text-gray-900">This evaluation</h4>
          {neededKeys.length === 0 ? (
            <p className="text-sm text-gray-500">Not enough codes to key a template yet.</p>
          ) : (
            <div className="space-y-3">
              {missingForEval.length > 0 && (
                <p className="text-sm text-amber-800">
                  Missing prose for {missingForEval.length} key{missingForEval.length === 1 ? '' : 's'} used by this eval.
                </p>
              )}
              {neededKeys.map(renderKeyEditor)}
            </div>
          )}
        </div>

        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-medium text-gray-900">Fill other missing permutations</h4>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">Group</span>
              <select
                className="rounded-md border border-gray-300 py-1.5 pl-2 pr-8 text-sm"
                onChange={(event) => {
                  setCatalogGroup(event.target.value);
                  setCatalogKey(null);
                  setUnlockedKey(null);
                }}
                value={catalogGroup}
              >
                <option value="CS">Caution (CS)</option>
                {REPORT_GROUPS.map((group) => (
                  <option key={group.id} value={group.id}>{group.label}</option>
                ))}
              </select>
            </label>
            <p className="text-sm text-gray-500">
              {catalogMissing.length} missing of {catalogKeys.length}
            </p>
            {catalogMissing.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  className="rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40"
                  disabled={Boolean(unlockedKey)}
                  onClick={() => stepCatalog(-1)}
                  title={unlockedKey ? 'Save or cancel the open field first' : undefined}
                  type="button"
                >
                  Previous missing
                </button>
                <button
                  className="rounded-md bg-white px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40"
                  disabled={Boolean(unlockedKey)}
                  onClick={() => stepCatalog(1)}
                  title={unlockedKey ? 'Save or cancel the open field first' : undefined}
                  type="button"
                >
                  Next missing
                </button>
              </div>
            )}
          </div>
          <div className="flex max-h-40 flex-wrap gap-2 overflow-auto rounded-md border border-gray-200 p-2">
            {catalogMissing.length === 0 ? (
              <p className="text-sm text-gray-500">No missing keys in this group.</p>
            ) : catalogMissing.map((key) => (
              <button
                className={classNames(
                  'rounded-md px-2 py-1 text-xs ring-1 ring-inset disabled:opacity-40',
                  catalogKey === key ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
                )}
                aria-pressed={catalogKey === key}
                disabled={Boolean(unlockedKey)}
                key={key}
                onClick={() => setCatalogKey(key)}
                title={unlockedKey ? 'Save or cancel the open field first' : undefined}
                type="button"
              >
                {key}
              </button>
            ))}
          </div>
          {catalogKey && renderKeyEditor(catalogKey)}
        </div>

        <div className="mt-6">
          <h4 className="text-sm font-medium text-gray-900">Draft preview</h4>
          <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-gray-50 p-3 text-xs text-gray-800">
            {assembledPreview.sections.map((section) => (
              `${section.key}\n${section.missing ? '(missing)' : section.text}`
            )).join('\n\n') || 'Nothing to preview yet.'}
          </pre>
        </div>

        {canEdit && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                checked={previewed}
                onChange={(event) => setPreviewed(event.target.checked)}
                type="checkbox"
              />
              I previewed this draft
            </label>
            <button
              className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-indigo-300"
              disabled={busy || !previewed}
              onClick={handlePublish}
              type="button"
            >
              Publish new version
            </button>
          </div>
        )}
      </section>

      <section>
        <h3 className="text-base font-medium text-gray-900">Generate</h3>
        <p className="mt-1 text-sm text-gray-600">
          Assembles a PDF from the immutable published version <code className="rounded bg-gray-100 px-1">{'{sex}_vN'}</code>,
          then writes a history doc with codes + templateVersion + PDF/ref. History never re-reads latest prose.
          Empty female prose is still a pin once v1 exists (seed insert-if-missing, or publish an empty draft).
        </p>
        {!canPinVersion && (
          <p className="mt-2 text-sm text-amber-800">
            Generate is disabled until a published template version exists to pin.
          </p>
        )}
        <button
          className="mt-3 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:bg-indigo-300"
          disabled={busy || !evaluationId || !canPinVersion}
          onClick={handleGenerate}
          type="button"
        >
          Generate PDF
        </button>
      </section>

      <section>
        <h3 className="text-base font-medium text-gray-900">History</h3>
        <p className="mt-1 text-sm text-gray-600">
          Each row is the stored generation document (pinned codes and version), not the current template.
        </p>
        {reportsError && <p className="mt-2 text-sm text-red-700">{reportsError}</p>}
        {reports.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">No generated reports for this evaluation yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-gray-200 rounded-md border border-gray-200">
            {reports.map((report) => (
              <li className="flex flex-wrap items-center justify-between gap-3 px-3 py-3 text-sm" key={report.id}>
                <div>
                  <p className="font-medium text-gray-900">{formatTimestamp(report.createdAt)}</p>
                  <p className="text-gray-600">
                    Template v{report.templateVersion ?? '—'} · {report.sex || 'unknown sex'}
                    {report.templateId ? ` · ${report.templateId}` : ''}
                  </p>
                  <p className="mt-1 text-xs text-gray-500">{formatPinnedCodesSummary(report.codes)}</p>
                </div>
                {report.pdfUrl ? (
                  <a className="font-medium text-indigo-600 hover:text-indigo-800" href={report.pdfUrl} rel="noreferrer" target="_blank">
                    Open PDF
                  </a>
                ) : (
                  <span className="text-gray-500">Local generation (no stored URL)</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {status && (
        <p className={classNames('text-sm', status.tone === 'error' ? 'text-red-700' : 'text-green-700')} role="status">
          {status.text}
        </p>
      )}
    </div>
  );
}
